import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Categories.css';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import Navbar from '../components/navbar/Navbar';
import Footer from '../components/footer/Footer';
import { useApp } from '../context/AppContext';
import { getApiEndpoint } from '../config/api';

const Categories = () => {
    const navigate = useNavigate();
    const { t } = useApp();
    const headerRef = useScrollAnimation('fade-up', 0.1, 0);
    const gridRef = useScrollAnimation('fade-up', 0.1, 200);
    const [hoveredCategory, setHoveredCategory] = useState(null);
    const [categoryCounts, setCategoryCounts] = useState({});

    // Fetch announcement counts for each category
    useEffect(() => {
        const fetchCategoryCounts = async () => {
            try {
                const response = await fetch(getApiEndpoint('/api/announcements/all'));
                const data = await response.json();
                const announcements = data.announcements || [];
                
                console.log('📊 Fetched announcements:', announcements.length);
                
                const counts = {};
                
                // Helper function to normalize category names for matching
                const normalizeCategory = (cat) => {
                    if (!cat) return '';
                    return cat.toLowerCase().trim()
                        .replace(/\s+/g, '-')
                        .replace(/ă/g, 'a')
                        .replace(/â/g, 'a')
                        .replace(/î/g, 'i')
                        .replace(/ș/g, 's')
                        .replace(/ț/g, 't');
                };
                
                // Define category mappings
                const categoryMappings = {
                    'utilaje-constructii': ['utilaje de constructii', 'utilaje de construcții', 'constructii', 'construcții'],
                    'transport': ['camioane', 'camioane (transport)', 'transport'],
                    'utilaje-agricole': ['utilaje agricole', 'agricole'],
                    'autoturisme-autoutilitare': ['autoturisme', 'autoturisme si autoutilitare', 'autoturisme și autoutilitare', 'autoutilitare']
                };
                
                // Count announcements for each category
                announcements.forEach(announcement => {
                    const category = announcement.category?.toLowerCase().trim() || '';
                    const subcategory = announcement.subcategory?.toLowerCase().trim() || '';
                    const subsubcategory = announcement.subsubcategory?.toLowerCase().trim() || '';
                    
                    console.log('📋 Processing announcement:', {
                        title: announcement.title,
                        category,
                        subcategory,
                        subsubcategory
                    });
                    
                    // Match main categories
                    Object.keys(categoryMappings).forEach(catId => {
                        const mappings = categoryMappings[catId];
                        if (mappings.some(mapping => category.includes(mapping) || mapping.includes(category))) {
                            counts[catId] = (counts[catId] || 0) + 1;
                        }
                    });
                    
                    // Count for subcategories (normalized)
                    if (subcategory) {
                        const normalizedSub = normalizeCategory(subcategory);
                        counts[normalizedSub] = (counts[normalizedSub] || 0) + 1;
                        counts[subcategory] = (counts[subcategory] || 0) + 1;
                    }
                    
                    // Count for sub-subcategories (normalized)
                    if (subsubcategory) {
                        const normalizedSubSub = normalizeCategory(subsubcategory);
                        counts[normalizedSubSub] = (counts[normalizedSubSub] || 0) + 1;
                        counts[subsubcategory] = (counts[subsubcategory] || 0) + 1;
                    }
                });
                
                console.log('📊 Final category counts:', counts);
                setCategoryCounts(counts);
            } catch (error) {
                console.error('Error fetching category counts:', error);
            }
        };

        fetchCategoryCounts();
    }, []);

    const categories = [
        {
            id: 'utilaje-constructii',
            name: 'UTILAJE DE CONSTRUCȚII',
            description: 'Excavatoare, buldozere și mai mult',
            image: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&h=600&fit=crop&q=80',
            subcategories: [
                { id: 'excavatoare', name: 'Excavatoare', 
                  items: ['excavatoare pe șenile', 'excavatoare pe roți', 'excavatoare pentru fier vechi', 'mini excavatoare'] 
                },
                { id: 'buldoexcavatoare', name: 'Buldoexcavatoare' },
                { id: 'buldozere', name: 'Buldozere' },
                { id: 'cilindri-compactori', name: 'Cilindri compactori',
                  items: ['Cilindri compactori', 'Cilindri mici', 'Cilindri tandem', 'Cilindri Polipneu']
                },
                { id: 'gredere', name: 'Gredere' },
                { id: 'incarcatoare', name: 'Încărcătoare',
                  items: ['Încărcătoare frontale', 'Încărcătoare telescopice', 'Mini încărcătoare']
                },
                { id: 'dumpere', name: 'Dumpere' },
                { id: 'automacarale', name: 'Automacarale' },
                { id: 'autobetoniere', name: 'Autobetoniere' },
                { id: 'masini-asfalt', name: 'Mașini de asfalt' },
                { id: 'sivuitoare', name: 'Sivuitoare' },
                { id: 'nacele', name: 'Nacele' },
                { id: 'statii-sortare', name: 'Stații de sortare' },
                { id: 'concasoare', name: 'Concasoare' },
                { id: 'accesorii-echipamente', name: 'Accesorii și echipamente' },
                { id: 'dezmembrari', name: 'Dezmembrări utilaje' },
                { id: 'diverse', name: 'Diverse' }
            ]
        },
        {
            id: 'transport',
            name: 'CAMIOANE (TRANSPORT)',
            description: 'Camioane și vehicule transport',
            image: 'https://images.unsplash.com/photo-1519003722824-194d4455a60c?w=800&h=600&fit=crop&q=80',
            subcategories: [
                { id: 'camioane', name: 'Camioane' },
                { id: 'trailere', name: 'Trailere' },
                { id: 'semiremorci', name: 'Semiremorci' },
                { id: 'matura-stradala', name: 'Mătură stradala' }
            ]
        },
        {
            id: 'utilaje-agricole',
            name: 'UTILAJE AGRICOLE',
            description: 'Tractoare și echipamente agricole',
            image: 'https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=800&h=600&fit=crop&q=80',
            subcategories: [
                { id: 'tractoare', name: 'Tractoare' },
                { id: 'combine', name: 'Combine' },
                { id: 'echipamente-agricole', name: 'Echipamente agricole' }
            ]
        },
        {
            id: 'autoturisme-autoutilitare',
            name: 'AUTOTURISME ȘI AUTOUTILITARE',
            description: 'Autoturisme, camionete și remorci',
            image: 'https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?w=800&h=600&fit=crop&q=80',
            subcategories: [
                { id: 'autoturisme', name: 'Autoturisme' },
                { id: 'autoutilitare', name: 'Autoutilitare (camionete)' },
                { id: 'remorci', name: 'Remorci' }
            ]
        }
    ];

    const handleCategoryClick = (categoryId) => {
        navigate(`/machines?category=${categoryId}`);
    };

    const handleSubcategoryClick = (subcategoryId, event) => {
        event.stopPropagation();
        navigate(`/machines?category=${subcategoryId}`);
    };

    const handleMoreHover = (event, categoryId) => {
        setHoveredCategory(categoryId);
    };

    const handleMoreLeave = () => {
        setHoveredCategory(null);
    };

    return (
        <div className="categories-page">
            <Navbar />
            
            {/* Header Section */}
            <div className="categories-header" ref={headerRef}>
                <div className="container">
                    <div className="header-content">
                        <h1 className="header-title">{t.categoriesPage.title}</h1>
                        <p className="header-subtitle">
                            {t.categoriesPage.subtitle}
                        </p>
                    </div>
                </div>
            </div>

            {/* Categories Grid */}
            <div className="categories-content" ref={gridRef}>
                <div className="container">
                    <div className="categories-grid">
                        {categories.map((category, index) => (
                            <div 
                                key={category.id} 
                                className="category-card"
                                style={{ animationDelay: `${index * 0.05}s` }}
                                onClick={() => handleCategoryClick(category.id)}
                            >
                                <div className="category-image-wrapper">
                                    <img 
                                        src={category.image} 
                                        alt={category.name}
                                        className="category-image"
                                    />
                                    <div className="category-overlay"></div>
                                </div>
                                <div className="category-content">
                                    <h3 className="category-title">{category.name}</h3>
                                    <p className="category-description">{category.description}</p>
                                    
                                    {/* Subcategories List */}
                                    {category.subcategories && category.subcategories.length > 0 && (
                                        <div className="subcategories-display">
                                            {category.subcategories.slice(0, 6).map((subcat, idx) => (
                                                <button
                                                    key={subcat.id}
                                                    className="subcategory-tag"
                                                    onClick={(e) => handleSubcategoryClick(subcat.id, e)}
                                                >
                                                    {idx + 1}. {subcat.name}
                                                </button>
                                            ))}
                                            {category.subcategories.length > 6 && (
                                                <div 
                                                    className="more-subcategories-wrapper"
                                                    onMouseEnter={(e) => handleMoreHover(e, category.id)}
                                                    onMouseLeave={handleMoreLeave}
                                                >
                                                    <span className="more-subcategories">
                                                        +{category.subcategories.length - 6} mai multe
                                                    </span>
                                                    {hoveredCategory === category.id && (
                                                        <div className="more-subcategories-dropdown">
                                                            {category.subcategories.slice(6).map((subcat, idx) => (
                                                                <button
                                                                    key={subcat.id}
                                                                    className="subcategory-tag-dropdown"
                                                                    onClick={(e) => handleSubcategoryClick(subcat.id, e)}
                                                                >
                                                                    {idx + 7}. {subcat.name}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    
                                    <div className="category-meta">
                                        <span className="category-count">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                <rect x="3" y="3" width="7" height="7" strokeWidth="2"/>
                                                <rect x="14" y="3" width="7" height="7" strokeWidth="2"/>
                                                <rect x="14" y="14" width="7" height="7" strokeWidth="2"/>
                                                <rect x="3" y="14" width="7" height="7" strokeWidth="2"/>
                                            </svg>
                                            {categoryCounts[category.id] || 0} {t.common?.announcements || 'anunțuri'}
                                        </span>
                                        <span className="category-arrow">→</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            
            <Footer />
        </div>
    );
};

export default Categories;
